import { useRef, useEffect, useState, useMemo } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import InteractionHints from './InteractionHints';
import { applyFabricTexture, updateTextureZoom, remapExtrudeUVs, setMaxAnisotropy } from './fabricTexture';
import FabricLoadingOverlay from './FabricLoadingOverlay';

const _isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
const DEFAULT_CAM = _isMobile ? { x: 0, y: 0.85, z: 0.91 } : { x: 0, y: 0.88, z: 1.05 };
const SCALE = 0.008;

function createTrapGeometry(sA, sD, sT, sLeftOff, sRightOff) {
  const halfA = sA / 2;
  const halfD = sD / 2;

  // 4 corners of the trapezoid
  const corners = [
    { x: -halfA, y: -halfD },
    { x: halfA, y: -halfD },
    { x: halfA - sRightOff, y: halfD },
    { x: -halfA + sLeftOff, y: halfD },
  ];

  // Corner radius — proportional but clamped
  const r = Math.min(sA * 0.06, sD * 0.10, 0.035);

  // Compute before/after points for rounded corners
  const pts = [];
  for (let i = 0; i < 4; i++) {
    const c = corners[i];
    const p = corners[(i + 3) % 4];
    const n = corners[(i + 1) % 4];
    const d1x = c.x - p.x, d1y = c.y - p.y;
    const l1 = Math.sqrt(d1x * d1x + d1y * d1y) || 1;
    const d2x = n.x - c.x, d2y = n.y - c.y;
    const l2 = Math.sqrt(d2x * d2x + d2y * d2y) || 1;
    const lr = Math.min(r, l1 * 0.35, l2 * 0.35);
    pts.push({
      bx: c.x - d1x / l1 * lr, by: c.y - d1y / l1 * lr,
      cx: c.x, cy: c.y,
      ax: c.x + d2x / l2 * lr, ay: c.y + d2y / l2 * lr,
    });
  }

  const shape = new THREE.Shape();
  shape.moveTo(pts[0].ax, pts[0].ay);
  for (let i = 1; i <= 4; i++) {
    const p = pts[i % 4];
    shape.lineTo(p.bx, p.by);
    shape.quadraticCurveTo(p.cx, p.cy, p.ax, p.ay);
  }

  const bevel = Math.min(sT * 0.1, 0.006);
  const geo = new THREE.ExtrudeGeometry(shape, {
    depth: sT,
    bevelEnabled: true,
    bevelThickness: bevel,
    bevelSize: bevel,
    bevelSegments: 3,
  });

  geo.rotateX(-Math.PI / 2);
  geo.translate(0, -sT / 2, 0);
  remapExtrudeUVs(geo);
  fixBayWindowWallUVs(geo);
  geo.computeVertexNormals();
  return geo;
}

// Fix wall UVs so fabric stripes run vertically (up/down) on ALL walls,
// matching the top face direction. Same approach as Chair Cushion.
function fixBayWindowWallUVs(geo) {
  const uvAttr = geo.attributes.uv;
  const posAttr = geo.attributes.position;
  const nAttr = geo.attributes.normal;
  if (!uvAttr || !posAttr || !nAttr) return;
  const inv = 1 / 0.8;
  for (let i = 0; i < uvAttr.count; i++) {
    const ny = Math.abs(nAttr.getY(i));
    if (ny > 0.5) continue;
    const px = posAttr.getX(i);
    const py = posAttr.getY(i);
    const pz = posAttr.getZ(i);
    const nxAbs = Math.abs(nAttr.getX(i));
    const nzAbs = Math.abs(nAttr.getZ(i));
    const total = nxAbs + nzAbs || 1;
    const u = (nxAbs * pz + nzAbs * px) / total * inv;
    const v = py * inv;
    uvAttr.setXY(i, u, v);
  }
  uvAttr.needsUpdate = true;
}

const BayWindow3DPreview = ({
  edgeA = 100, edgeB = 60, depth = 50,
  edgeD = 0, edgeE = 0, thickness = 5, rawThickness,
  fabricColor, fabricImageUrl, fabricZoom = 2, showZip = true, rawEdgeA = 0, rawEdgeB = 0, rawDepth = 0, unit = 'cm',
  activeField = null, pipingOption = 'none', pipingColor = null, pipingImageUrl = null
}) => {
  const containerRef = useRef(null);
  const [fabricLoading, setFabricLoading] = useState(false);
  const [pipingLoading, setPipingLoading] = useState(false);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const cushionRef = useRef(null);
  const cornerMeshesRef = useRef([]);
  const zipGroupRef = useRef(null);
  const pipingTopRef = useRef(null);
  const pipingBottomRef = useRef(null);
  const animFrameRef = useRef(null);
  const initializedRef = useRef(false);
  const resetCameraRef = useRef(false);
  const containerWidthRef = useRef(600);
  const dimLabelsRef = useRef({ a: null, b: null, depth: null, thickness: null, d: null, e: null });
  const currentDimsRef = useRef(null);
  const targetDimsRef = useRef(null);
  const targetScaleYRef = useRef(1);
  const activeFieldRef = useRef(activeField);
  const groundRef = useRef(null);
  const depthLineRef = useRef(null);  // SVG line element ref
  const zipDashRef = useRef(null);

  useEffect(() => { activeFieldRef.current = activeField; }, [activeField]);

  const color = fabricColor || '#5a8a5a';

  const dims = useMemo(() => {
    const sA = Math.max(edgeA * SCALE, 0.15);
    const sB = Math.max(edgeB * SCALE, 0.08);
    const sD = Math.max(depth * SCALE, 0.1);
    const sT = Math.max(thickness * SCALE, 0.03);

    const diff = edgeA - edgeB;
    let leftOff = 0, rightOff = 0;

    if (diff > 0 && edgeD > 0 && edgeE > 0 && depth > 0) {
      const loSq = edgeD * edgeD - depth * depth;
      const roSq = edgeE * edgeE - depth * depth;
      if (loSq >= 0 && roSq >= 0) {
        leftOff = Math.sqrt(loSq) * SCALE;
        rightOff = Math.sqrt(roSq) * SCALE;
      } else {
        leftOff = diff * SCALE / 2;
        rightOff = diff * SCALE / 2;
      }
    } else if (diff > 0) {
      leftOff = diff * SCALE / 2;
      rightOff = diff * SCALE / 2;
    }

    return { a: sA, b: sB, d: sD, t: sT, leftOff, rightOff };
  }, [edgeA, edgeB, depth, edgeD, edgeE, thickness]);

  // Initialize scene once
  useEffect(() => {
    if (!containerRef.current || initializedRef.current) return;
    initializedRef.current = true;

    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    containerWidthRef.current = rect.width;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(35, rect.width / rect.height, 0.01, 100);
    camera.position.set(DEFAULT_CAM.x, DEFAULT_CAM.y, DEFAULT_CAM.z);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setSize(rect.width, rect.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enablePan = true;
    controls.screenSpacePanning = true;
    controls.panSpeed = 1.2;
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.minDistance = 0.15;
    controls.maxDistance = 4;
    controls.minPolarAngle = 0;
    controls.maxPolarAngle = Math.PI;
    controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };
    controls.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.PAN, RIGHT: THREE.MOUSE.PAN };
    controls.target.set(0, 0, 0);
    controls.addEventListener('start', () => { resetCameraRef.current = false; });
    renderer.domElement.__orbitControls = controls;
    renderer.domElement.__renderFrame = () => renderer.render(scene, camera);
    controlsRef.current = controls;

    // Lighting
    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    const dirLight = new THREE.DirectionalLight(0xffffff, 1.2);
    dirLight.position.set(3, 5, 3);
    dirLight.castShadow = true;
    dirLight.shadow.mapSize.set(1024, 1024);
    scene.add(dirLight);
    scene.add(new THREE.DirectionalLight(0xffffff, 0.4).translateX(-2).translateY(3).translateZ(-1));
    scene.add(new THREE.PointLight(0xffffff, 0.3).translateY(2));

    // Shadow ground
    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(5, 5),
      new THREE.ShadowMaterial({ opacity: 0.15 })
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.05;
    ground.receiveShadow = true;
    scene.add(ground);
    groundRef.current = ground;

    // Cushion
    const { a, d, t, leftOff, rightOff } = dims;
    const geo = createTrapGeometry(a, d, t, leftOff, rightOff);
    const cushion = new THREE.Mesh(
      geo,
      new THREE.MeshStandardMaterial({ color: new THREE.Color(color), roughness: 0.85, metalness: 0.02 })
    );
    cushion.castShadow = true;
    scene.add(cushion);
    cushionRef.current = cushion;
    currentDimsRef.current = { ...dims };
    targetDimsRef.current = { ...dims };

    // Corner seams removed — rounded corners provide sufficient visual definition
    cornerMeshesRef.current = [];

    // Zip: 3 track meshes + 24 teeth + 1 pull (like Premium, across 3-segment path)
    const zipMat = new THREE.MeshBasicMaterial({ color: 0xf5f5f5 });
    const zipTracks = [];
    for (let i = 0; i < 3; i++) {
      const track = new THREE.Mesh(new THREE.BoxGeometry(1, 0.006, 0.008), zipMat);
      scene.add(track);
      zipTracks.push(track);
    }
    const zipTeeth = [];
    for (let i = 0; i < 48; i++) {
      const tooth = new THREE.Mesh(new THREE.BoxGeometry(0.01, 0.006, 0.006), zipMat);
      scene.add(tooth);
      zipTeeth.push(tooth);
    }
    const zipPull = new THREE.Mesh(
      new THREE.BoxGeometry(0.03, 0.005, 0.006), zipMat
    );
    scene.add(zipPull);
    zipDashRef.current = { tracks: zipTracks, teeth: zipTeeth, pull: zipPull };

    // 3D depth dimension line (dashes + arrow cones, properly occluded by cushion)
    const dimLineMat = new THREE.MeshBasicMaterial({ color: 0xcccccc });
    const depthDashes = [];
    for (let i = 0; i < 14; i++) {
      const dash = new THREE.Mesh(new THREE.BoxGeometry(0.003, 0.003, 0.008), dimLineMat);
      scene.add(dash);
      depthDashes.push(dash);
    }
    const dimArrowGeo = new THREE.ConeGeometry(0.007, 0.015, 6);
    const depthArrowFront = new THREE.Mesh(dimArrowGeo, dimLineMat);
    const depthArrowBack = new THREE.Mesh(dimArrowGeo, dimLineMat);
    scene.add(depthArrowFront);
    scene.add(depthArrowBack);
    depthLineRef.current = { dashes: depthDashes, arrowFront: depthArrowFront, arrowBack: depthArrowBack, mat: dimLineMat };

    // Piping rings (top + bottom edges)
    const pipingMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.6, metalness: 0.05 });
    const pipingTop = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat);
    const pipingBottom = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat.clone());
    pipingTop.visible = false;
    pipingBottom.visible = false;
    scene.add(pipingTop);
    scene.add(pipingBottom);
    pipingTopRef.current = pipingTop;
    pipingBottomRef.current = pipingBottom;
    setMaxAnisotropy(renderer);

    // Animation loop
    const targetCamPos = new THREE.Vector3();
    const zeroVec = new THREE.Vector3(0, 0, 0);

    const animate = () => {
      animFrameRef.current = requestAnimationFrame(animate);
      // Thumbnail mode: hide dimension indicators and zip
      if (typeof window !== 'undefined' && window.__THUMBNAIL_MODE__) {
        const __dl = depthLineRef.current;
        if (__dl) Object.values(__dl).forEach(v => {
          if (Array.isArray(v)) v.forEach(m => { if (m && m.visible !== undefined) m.visible = false; });
          else if (v && v.visible !== undefined) v.visible = false;
        });

              const __zd = zipDashRef.current;
        if (__zd) {
          if (__zd.tracks) __zd.tracks.forEach(t => t.visible = false);
          if (__zd.teeth) __zd.teeth.forEach(t => t.visible = false);
          if (__zd.pull) __zd.pull.visible = false;
        }
}
      controls.update();

      const cd = currentDimsRef.current;
      const td = targetDimsRef.current;
      if (!cd || !td) return;

      // Smooth dimension lerping — rebuild geometry each frame when moving
      const lerpF = 0.1;
      const nextA = THREE.MathUtils.lerp(cd.a, td.a, lerpF);
      const nextB = THREE.MathUtils.lerp(cd.b, td.b, lerpF);
      const nextD = THREE.MathUtils.lerp(cd.d, td.d, lerpF);
      const nextT = THREE.MathUtils.lerp(cd.t, td.t, lerpF);
      const nextLeftOff = THREE.MathUtils.lerp(cd.leftOff, td.leftOff, lerpF);
      const nextRightOff = THREE.MathUtils.lerp(cd.rightOff, td.rightOff, lerpF);

      const changed = Math.abs(nextA - cd.a) > 0.00005 || Math.abs(nextB - cd.b) > 0.00005 ||
          Math.abs(nextD - cd.d) > 0.00005 || Math.abs(nextT - cd.t) > 0.00005 ||
          Math.abs(nextLeftOff - cd.leftOff) > 0.00005 || Math.abs(nextRightOff - cd.rightOff) > 0.00005;

      if (changed) {
        cushion.geometry.dispose();
        cushion.geometry = createTrapGeometry(nextA, nextD, nextT, nextLeftOff, nextRightOff);
        currentDimsRef.current = { a: nextA, b: nextB, d: nextD, t: nextT, leftOff: nextLeftOff, rightOff: nextRightOff };
      }

      // Re-read current dims after potential lerp update
      const cd2 = currentDimsRef.current;
      const halfA = cd2.a / 2;
      const halfD = cd2.d / 2;

      // Ground
      ground.position.y = -cd2.t / 2 - 0.002;

      // Zip: position 3 tracks (slight overlap) + 24 teeth along continuous path
      if (zipDashRef.current && zipDashRef.current.tracks) {
        const { tracks, teeth, pull } = zipDashRef.current;
        const visible = showZip;
        tracks.forEach(t => t.visible = visible);
        teeth.forEach(t => t.visible = visible);
        pull.visible = visible;
        if (visible) {
        const backLeftX = -halfA + cd2.leftOff;
        const backRightX = halfA - cd2.rightOff;
        const midDx = (-halfA + backLeftX) / 2;
        const midEx = (halfA + backRightX) / 2;

        const segPts = [
          { sx: midDx, sz: 0, ex: backLeftX, ez: -halfD },
          { sx: backLeftX, sz: -halfD, ex: backRightX, ez: -halfD },
          { sx: backRightX, sz: -halfD, ex: midEx, ez: 0 },
        ];

        // Position track segments with slight overlap at corners (no gaps)
        const segData = [];
        for (let s = 0; s < 3; s++) {
          const { sx, sz, ex, ez } = segPts[s];
          const dx = ex - sx, dz = ez - sz;
          const len = Math.sqrt(dx * dx + dz * dz) || 0.01;
          const angle = Math.atan2(dz, dx);
          // Unit outward normal
          const unx = dz / len, unz = -dx / len;

          tracks[s].position.set((sx + ex) / 2 + unx * 0.005, 0, (sz + ez) / 2 + unz * 0.005);
          tracks[s].rotation.set(0, -angle, 0);
          tracks[s].scale.set(len * 1.04, 1, 1);
          segData.push({ sx, sz, ex, ez, len, angle, unx, unz });
        }

        // Distribute 24 teeth evenly along the total path
        const totalLen = segData[0].len + segData[1].len + segData[2].len;
        const toothSpacing = totalLen / teeth.length;
        for (let i = 0; i < teeth.length; i++) {
          let dist = (i + 0.5) * toothSpacing;
          let cumLen = 0, si = 0;
          for (let s = 0; s < 3; s++) {
            if (cumLen + segData[s].len >= dist || s === 2) { si = s; break; }
            cumLen += segData[s].len;
          }
          const t = segData[si].len > 0 ? (dist - cumLen) / segData[si].len : 0.5;
          const px = segData[si].sx + t * (segData[si].ex - segData[si].sx);
          const pz = segData[si].sz + t * (segData[si].ez - segData[si].sz);
          // Teeth alternate perpendicular to the track on both sides
          const side = (i % 2 === 0 ? 1 : -1) * 0.010;
          teeth[i].position.set(px + segData[si].unx * side, 0, pz + segData[si].unz * side);
          teeth[i].rotation.set(0, -segData[si].angle, 0);
        }

        // Pull at midE end
        const last = segData[2];
        pull.position.set(last.ex + last.unx * 0.008, 0.004, last.ez + last.unz * 0.008);
        pull.rotation.set(0, -last.angle, 0);
        }
      }

      // 3D depth dimension line positioning
      if (depthLineRef.current && depthLineRef.current.dashes) {
        const { dashes, arrowFront, arrowBack } = depthLineRef.current;
        const lineX = -cd2.a * 0.15;
        const topY = cd2.t / 2 + 0.006;
        for (let i = 0; i < dashes.length; i++) {
          const t = (i + 0.5) / dashes.length;
          const z = halfD - cd2.d * t;
          dashes[i].position.set(lineX, topY, z);
        }
        const arrowHalf = 0.0075;
        arrowFront.position.set(lineX, topY, halfD - arrowHalf);
        arrowFront.rotation.set(Math.PI / 2, 0, 0);
        arrowBack.position.set(lineX, topY, -halfD + arrowHalf);
        arrowBack.rotation.set(-Math.PI / 2, 0, 0);
      }

      // Camera reset — FOV-based framing to always see entire cushion
      if (resetCameraRef.current) {
        const cc = currentDimsRef.current;
        const maxDim = Math.max(cc.a, cc.d, cc.a * 0.8 + cc.d * 0.5);
        const isMobile = containerWidthRef.current < 500;
        // Calculate required distance based on FOV to fit entire cushion
        const fovRad = (35 / 2) * Math.PI / 180;
        const margin = isMobile ? 1.03 : 0.95;
        const requiredDist = (maxDim / (2 * Math.tan(fovRad))) * margin;
        // Direction from DEFAULT_CAM
        const dirLen = Math.sqrt(DEFAULT_CAM.x ** 2 + DEFAULT_CAM.y ** 2 + DEFAULT_CAM.z ** 2);
        targetCamPos.set(
          (DEFAULT_CAM.x / dirLen) * requiredDist,
          (DEFAULT_CAM.y / dirLen) * requiredDist,
          (DEFAULT_CAM.z / dirLen) * requiredDist
        );
        camera.position.lerp(targetCamPos, 0.12);
        controls.target.lerp(zeroVec, 0.12);
        if (camera.position.distanceTo(targetCamPos) < 0.02) {
          resetCameraRef.current = false;
          controls.saveState();
        }
      }

      renderer.render(scene, camera);

      // Project dimension labels to 2D + highlight active field
      const af = activeFieldRef.current;
      const YELLOW = 0xffd700;
      const activeBg = 'rgba(255,215,0,0.92)';
      const defaultBg = 'rgba(255,255,255,0.85)';
      const activeText = '#1a1a1a';
      const defaultText = '#374151';

      // Highlight depth line
      if (depthLineRef.current && depthLineRef.current.mat) {
        depthLineRef.current.mat.color.set(af === 'depth' ? YELLOW : 0xcccccc);
      }

      const labels = dimLabelsRef.current;
      if (labels.a && labels.b && labels.depth && labels.d && labels.e) {
        const cRect = container.getBoundingClientRect();
        const project = (wp) => {
          const v = wp.clone().project(camera);
          return {
            x: (v.x * 0.5 + 0.5) * cRect.width,
            y: (-v.y * 0.5 + 0.5) * cRect.height,
            visible: v.z < 1
          };
        };

        const setLabelHighlight = (el, isActive, pos) => {
          el.style.left = pos.x + 'px';
          el.style.top = pos.y + 'px';
          el.style.opacity = pos.visible ? '1' : '0';
          el.style.backgroundColor = isActive ? activeBg : defaultBg;
          el.style.color = isActive ? activeText : defaultText;
        };

        // A: front edge midpoint
        const aP = project(new THREE.Vector3(0, -cd2.t / 2 - 0.01, halfD + 0.02));
        setLabelHighlight(labels.a, af === 'edgeA', aP);

        // B: back edge midpoint
        const backCX = (cd2.leftOff - cd2.rightOff) / 2;
        const bP = project(new THREE.Vector3(backCX, -cd2.t / 2 - 0.01, -halfD - 0.02));
        setLabelHighlight(labels.b, af === 'edgeB', bP);

        // C: depth label next to the dotted line (shifted left), midpoint of depth
        const lineX = -cd2.a * 0.15;
        const cP = project(new THREE.Vector3(lineX - 0.03, cd2.t / 2 + 0.01, 0));
        setLabelHighlight(labels.depth, af === 'depth', cP);

        // D: left angled edge midpoint
        const dMidX = (-halfA + (-halfA + cd2.leftOff)) / 2;
        const dP = project(new THREE.Vector3(dMidX - 0.03, -cd2.t / 2 - 0.01, 0));
        setLabelHighlight(labels.d, af === 'edgeD', dP);

        // E: right angled edge midpoint
        const eMidX = (halfA + (halfA - cd2.rightOff)) / 2;
        const eP = project(new THREE.Vector3(eMidX + 0.03, -cd2.t / 2 - 0.01, 0));
        setLabelHighlight(labels.e, false, eP);
      }
    };
    animate();

    const handleResize = () => {
      if (!container) return;
      const r = container.getBoundingClientRect();
      containerWidthRef.current = r.width;
      camera.aspect = r.width / r.height;
      camera.updateProjectionMatrix();
      renderer.setSize(r.width, r.height);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      controls.dispose();
      renderer.dispose();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      initializedRef.current = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Set target dimensions — animation loop handles smooth lerp
  useEffect(() => {
    if (!targetDimsRef.current) return;
    targetDimsRef.current = { ...dims };
    resetCameraRef.current = true;
  }, [dims]);

  // Update color / texture
  useEffect(() => {
    if (cushionRef.current) {
      const needsLoad = fabricImageUrl && (fabricImageUrl.includes('warwick.com') || fabricImageUrl.startsWith('/api/fabric-images'));
      if (needsLoad) setFabricLoading(true);
      applyFabricTexture(cushionRef.current, fabricImageUrl, color, fabricZoom, () => setFabricLoading(false));
    }
  }, [color, fabricImageUrl]);

  // Update texture zoom
  useEffect(() => {
    if (cushionRef.current) updateTextureZoom(cushionRef.current, fabricZoom);
  }, [fabricZoom]);

  // Update piping rings
  useEffect(() => {
    const pt = pipingTopRef.current;
    const pb = pipingBottomRef.current;
    if (!pt || !pb) return;

    const showPiping = pipingOption !== 'none';
    pt.visible = showPiping;
    pb.visible = showPiping;
    if (!showPiping) { setPipingLoading(false); return; }

    // Piping color/texture
    const pColor = pipingOption === 'same' ? (color || '#5a8a5a') : (pipingColor || '#f5f0eb');
    const pImageUrl = pipingOption === 'same' ? fabricImageUrl : pipingImageUrl;
    const needsLoad = pImageUrl && (pImageUrl.includes('warwick.com') || pImageUrl.startsWith('/api/fabric-images'));
    if (needsLoad) setPipingLoading(true);
    let __remaining = 2;
    const __done = () => { __remaining -= 1; if (__remaining <= 0) setPipingLoading(false); };
    applyFabricTexture(pt, pImageUrl, pColor, fabricZoom, needsLoad ? __done : null);
    applyFabricTexture(pb, pImageUrl, pColor, fabricZoom, needsLoad ? __done : null);

    // Get current animated dims for piping path
    const cd = currentDimsRef.current || dims;
    const sA = cd.a;
    const sD = cd.d;
    const sT = cd.t;
    const sLeftOff = cd.leftOff;
    const sRightOff = cd.rightOff;

    const halfA = sA / 2;
    const halfD = sD / 2;

    // Trapezoid corners in SHAPE space (X-Y plane)
    // After rotateX(-PI/2): shape X → world X, shape Y → world -Z
    // Expand outline to bevel's 45° midpoint so piping sits on the edge
    const r = Math.min(sA * 0.06, sD * 0.10, 0.035);
    const tubeRadius = 0.003;
    const bevel = Math.min(sT * 0.1, 0.006);
    const sin45 = Math.SQRT1_2;
    const bevelExpand = bevel * sin45;
    const corners = [
      { x: -halfA - bevelExpand, y: -halfD - bevelExpand },
      { x: halfA + bevelExpand, y: -halfD - bevelExpand },
      { x: halfA - sRightOff + bevelExpand, y: halfD + bevelExpand },
      { x: -halfA + sLeftOff - bevelExpand, y: halfD + bevelExpand },
    ];

    // Compute rounded corner data (same as createTrapGeometry)
    const cornerData = [];
    for (let i = 0; i < 4; i++) {
      const c = corners[i];
      const p = corners[(i + 3) % 4];
      const n = corners[(i + 1) % 4];
      const d1x = c.x - p.x, d1y = c.y - p.y;
      const l1 = Math.sqrt(d1x * d1x + d1y * d1y) || 1;
      const d2x = n.x - c.x, d2y = n.y - c.y;
      const l2 = Math.sqrt(d2x * d2x + d2y * d2y) || 1;
      const lr = Math.min(r, l1 * 0.35, l2 * 0.35);
      cornerData.push({
        bx: c.x - d1x / l1 * lr, by: c.y - d1y / l1 * lr,
        cx: c.x, cy: c.y,
        ax: c.x + d2x / l2 * lr, ay: c.y + d2y / l2 * lr,
      });
    }

    // Build piping path in WORLD space (shape Y negated to world Z)
    const pts = [];
    const arcSegs = 32;

    for (let i = 0; i < 4; i++) {
      const curr = cornerData[i];
      const next = cornerData[(i + 1) % 4];

      // Straight edge: curr.after → next.before
      pts.push(new THREE.Vector3(curr.ax, 0, -curr.ay));
      pts.push(new THREE.Vector3(next.bx, 0, -next.by));

      // Quadratic bezier arc at next corner
      for (let j = 1; j <= arcSegs; j++) {
        const t = j / arcSegs;
        const t1 = 1 - t;
        const qx = t1 * t1 * next.bx + 2 * t1 * t * next.cx + t * t * next.ax;
        const qy = t1 * t1 * next.by + 2 * t1 * t * next.cy + t * t * next.ay;
        pts.push(new THREE.Vector3(qx, 0, -qy));
      }
    }

    // Custom linear closed curve — exact path, no CatmullRom distortion
    class LinearClosedCurve extends THREE.Curve {
      constructor(points) { super(); this.pts = points; }
      getPoint(t) {
        const n = this.pts.length;
        const f = t * n;
        const i = Math.floor(f) % n;
        const j = (i + 1) % n;
        const frac = f - Math.floor(f);
        return new THREE.Vector3().lerpVectors(this.pts[i], this.pts[j], frac);
      }
    }

    const curve = new LinearClosedCurve(pts);
    const tubeGeo = new THREE.TubeGeometry(curve, 256, tubeRadius, 8, true);

    pt.geometry.dispose();
    pb.geometry.dispose();
    pt.geometry = tubeGeo;
    pb.geometry = tubeGeo.clone();

    // Position at top/bottom edges — bevel's 45° midpoint
    const pipingY = sT / 2 + bevel * sin45;
    pt.position.y = pipingY;
    pb.position.y = -pipingY;
  }, [dims, pipingOption, pipingColor, pipingImageUrl, fabricImageUrl, color, fabricZoom]);

  const handleReset = () => { resetCameraRef.current = true; };

  return (
    <div
      className="w-full h-[280px] sm:h-[480px] rounded-xl overflow-hidden relative"
      style={{ background: 'linear-gradient(135deg, #f5f0eb 0%, #e8e0d8 100%)' }}
      data-testid="baywindow-3d-preview"
    >
      {fabricLoading && <FabricLoadingOverlay />}
      {!fabricLoading && pipingLoading && <FabricLoadingOverlay label="Loading piping..." />}
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} data-testid="three-canvas-container" />

      {/* Dimension labels projected from 3D */}
      <div
        ref={el => dimLabelsRef.current.a = el}
        className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm"
        style={{ opacity: 0, transition: 'opacity 0.15s' }}
        data-testid="dim-label-a"
      >
        {rawEdgeA > 0 ? `A: ${unit === 'mm' ? rawEdgeA * 10 : rawEdgeA} ${unit}` : `A ${unit}`}
      </div>
      <div
        ref={el => dimLabelsRef.current.b = el}
        className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm"
        style={{ opacity: 0, transition: 'opacity 0.15s' }}
        data-testid="dim-label-b"
      >
        {rawEdgeB > 0 ? `B: ${unit === 'mm' ? rawEdgeB * 10 : rawEdgeB} ${unit}` : `B ${unit}`}
      </div>
      <div
        ref={el => dimLabelsRef.current.depth = el}
        className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm"
        style={{ opacity: 0, transition: 'opacity 0.15s' }}
        data-testid="dim-label-depth"
      >
        {rawDepth > 0 ? `C: ${unit === 'mm' ? rawDepth * 10 : rawDepth} ${unit}` : `C ${unit}`}
      </div>
      <div
        ref={el => dimLabelsRef.current.d = el}
        className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm"
        style={{ opacity: 0, transition: 'opacity 0.15s' }}
        data-testid="dim-label-d"
      >
        {edgeD > 0 ? `D: ${unit === 'mm' ? Math.round(edgeD * 10) : Math.round(edgeD * 10) / 10} ${unit}` : `D ${unit}`}
      </div>
      <div
        ref={el => dimLabelsRef.current.e = el}
        className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm"
        style={{ opacity: 0, transition: 'opacity 0.15s' }}
        data-testid="dim-label-e"
      >
        {edgeE > 0 ? `E: ${unit === 'mm' ? Math.round(edgeE * 10) : Math.round(edgeE * 10) / 10} ${unit}` : `E ${unit}`}
      </div>

      {/* Zip legend */}
      {showZip && (
      <div className="hidden sm:flex absolute top-3 left-3 items-center gap-2 bg-white/85 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-gray-600 pointer-events-none" data-testid="zip-legend">
        <svg width="24" height="6" viewBox="0 0 24 6" className="flex-shrink-0">
          <line x1="0" y1="3" x2="24" y2="3" stroke="#333" strokeWidth="2" strokeDasharray="3,2" />
        </svg>
        <span className="font-medium">Zip on back edge</span>
      </div>
      )}

      {/* Reset view button */}
      <button
        onClick={handleReset}
        className="absolute top-3 right-3 w-8 h-8 bg-white/85 backdrop-blur-sm rounded-full flex items-center justify-center text-gray-500 hover:text-gray-800 hover:bg-white transition-colors shadow-sm cursor-pointer"
        title="Reset view"
        data-testid="reset-view-btn"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12a9 9 0 0 1-15.36 6.36"/>
          <path d="M3 12a9 9 0 0 1 15.36-6.36"/>
          <polyline points="21 3 21 9 15 9"/>
          <polyline points="3 21 3 15 9 15"/>
        </svg>
      </button>

      <InteractionHints className="hidden sm:flex absolute bottom-3 left-1/2 -translate-x-1/2 items-center gap-3 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-gray-500 pointer-events-none whitespace-nowrap" />
    </div>
  );
};

export default BayWindow3DPreview;
